// Strings of the networks list, New network, the Address plan, Private DNS, Entry points and Settings tabs (spec 11
// 6.15.2, 6.15.3, 6.15.8–6.15.10, 6.15.12). `strings.networks.lists`.
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export const networkLists = {
  // 6.15.2 Networks list
  list: {
    title: 'Networks',
    line: 'A network gives attached deployments non-overlapping addresses, private DNS and a topology. Deployments that aren’t attached keep their own standalone network.',
    filter: 'Filter networks',
    newNetwork: 'New network',
    columns: {
      network: 'Network',
      status: 'Status',
      drift: 'Drift',
      topology: 'Topology',
      providers: 'Providers and regions',
      attachments: 'Attachments',
      requests: 'Requests',
      entryPoints: 'Entry points',
      lastApply: 'Last apply',
      run: 'Run',
    },
    count: {
      connected: (n: number) => `${n} connected`,
      connecting: (n: number) => `${n} connecting`,
      pending: (n: number) => `${n} pending`,
    },
    noneYet: 'none yet',
    waiting: (n: number) => `${n} waiting for the network’s apply`,
    failed: (n: number) => `${n} failed`,
    retained: (n: number) => `${n} retained`,
    never: 'never',
    running: (kind: string) => `${kind} running`,
    queued: (n: number) => `${n} queued`,
    nextPlan: 'Next: plan the network',
    empty:
      'A network gives deployments addresses that don’t overlap, private DNS and a topology. Deployments keep working standalone until they’re attached.',
    askAdmin: 'Ask a network admin to create one.',
    noMatch: 'No network matches the filter.',
    regionChip: (provider: string, region: string) => `${provider} ${region}`,
    statusBar: (n: number) => plural(n, 'network'),
  },
  // 6.15.3 New network
  create: {
    title: 'New network',
    steps: 'Steps',
    step: (n: number, name: string) => `Step ${n} of 5: ${name}`,
    stepNames: [
      'Name',
      'Providers and regions',
      'Address plan',
      'Topology',
      'Allowed accounts and attach policy',
    ],
    back: 'Back',
    next: 'Next',
    create: 'Create',
    creating: 'Creating…',
    checking: 'Checking…',
    stepErrors: 'Fix the problems on this step to continue.',
    otherProblems: 'The check found these problems:',
    name: 'Name',
    nameRequired: 'Enter a name.',
    description: 'Description',
    descriptionHelp: 'Optional. Shown on the network page.',
    useProvider: (provider: string) => `Use ${provider}`,
    noProviders: 'No provider has networking switched on in this installation.',
    connectivityAccount: 'Connectivity account',
    connectivityHelp: 'The account that holds the network’s private DNS zones and hubs.',
    connectivityHelpCloudflare:
      'The Cloudflare account whose zones the network’s Cloudflare entry points use. Cloudflare has no regions.',
    chooseAccount: 'Choose an account',
    regions: (provider: string) => `${provider} regions`,
    regionsHelp: 'The regions attached deployments can use.',
    accountsNeedOperator: 'Listing cloud accounts needs the operator role.',
    accountById: (id: string) => `Account ${id}`,
    verified: 'verified',
    verificationFailed: 'verification failed',
    notVerified: 'not verified',
    allocationsTitle: 'Blocks this plan makes',
    allocation: (provider: string, region: string, blocks: string) => `${provider} · ${region}: ${blocks}`,
    allocationKind: (kind: string, cidr: string) => `${kind} ${cidr}`,
    noAllocations: 'Choose regions to see the blocks the plan makes.',
    topologyLegend: 'Topology',
    allowedLegend: (provider: string) => `${provider} accounts that may attach`,
    connectivityAlwaysAllowed: 'The connectivity account is always allowed.',
    whoMayAttach: 'Who may attach deployments',
    operators: 'Operators',
    operatorsHelp: 'Operators attach their deployments themselves.',
    networkAdmins: 'Only network admins',
    networkAdminsHelp: 'Only network admins attach deployments; operators ask them.',
    folded: (accounts: string, operators: boolean) =>
      `Deployments in ${accounts} may attach. ${operators ? 'Operators may attach.' : 'Only network admins attach.'}`,
    change: 'Change',
    created: (name: string) => `Network ${name} created`,
  },
  // Address plan fields (New network step 3, Edit address plan)
  plan: {
    supernets: 'Supernets',
    supernet: (i: number) => `Supernet ${i + 1}`,
    supernetsHelp: 'Private IPv4 ranges (CIDR, /8 to /20) the network allocates its blocks from.',
    suggested: 'Suggested: overlaps no other network.',
    addSupernet: 'Add supernet',
    removeSupernet: (cidr: string) => `Remove supernet ${cidr || 'row'}`,
    supernetInUse: (cidr: string) => `${cidr} holds allocations; it can’t be removed.`,
    reserved: 'Reserved ranges',
    reservedHelp: 'Ranges the network never allocates, for example on-premises networks.',
    reservedCidr: (i: number) => `Reserved range ${i + 1} CIDR`,
    reservedLabel: (i: number) => `Reserved range ${i + 1} label`,
    addReserved: 'Add reserved range',
    removeReserved: (i: number) => `Remove reserved range ${i + 1}`,
    noReserved: 'No reserved ranges.',
    regionPrefix: 'Region prefix length',
    regionPrefixHelp: '12 to 20: the size of each region’s block.',
    hubPrefix: 'Hub prefix length',
    hubPrefixHelp: '22 to 26: the size of each region’s hub block.',
    defaultSize: 'Default attachment size',
    invalidCidr: 'Enter an IPv4 CIDR, for example 10.64.0.0/12.',
    range: (min: number, max: number) => `Enter a number from ${min} to ${max}.`,
    labelRequired: 'Enter a label.',
  },
  // 6.15.8 Address plan tab
  addressPlan: {
    title: 'Address plan',
    supernet: 'Supernet',
    bar: (cidr: string, n: number, m: number, allocated: string, reserved: string) =>
      `Supernet ${cidr}: ${n} of ${m} region blocks allocated (${allocated} %) · reserved ${reserved} %`,
    allocatedLine: (n: number, m: number, allocated: string, reserved: string) =>
      `${n} of ${m} region blocks allocated (${allocated} %) · reserved ${reserved} %`,
    legendReserved: (cidr: string, label: string) => `reserved ${cidr} ${label}`,
    legendRegions: 'region blocks',
    legendLengths: (region: number, hub: number, size: string, prefix: number) =>
      `region /${region} · hub /${hub} · default size ${size} /${prefix}`,
    sideText:
      'Addresses never change when the topology changes. Released blocks are reusable after their quarantine.',
    edit: 'Edit',
    showHistory: 'Show history',
    showHistoryCount: (n: number) => `Show history (${n} released)`,
    openSettings: (deployment: string) => `Open ${deployment} settings`,
    columns: {
      allocation: 'Allocation',
      cidr: 'CIDR',
      kind: 'Kind / size',
      state: 'State',
      use: 'Use',
      need: 'Need vs capacity',
    },
    table: (network: string) => `Allocations of ${network}`,
    regionBlock: 'region block',
    allocatedPercent: (percent: string) => `${percent} % allocated`,
    hub: 'Hub',
    hubKind: 'hub',
    inUse: 'in use',
    reservedUse: 'reserved',
    hubSubnets: (inUse: string[], reserved: string[]) =>
      [
        inUse.length ? `${inUse.join(', ')} in use` : '',
        reserved.length ? `${reserved.join(', ')} reserved` : '',
      ]
        .filter(Boolean)
        .join(' · '),
    subnets: (n: number, used: number) => `${plural(n, 'subnet')} · ${used} in use`,
    tooSmall: (n: number) => `${plural(n, 'subnet')} too small`,
    need: (need: number, capacity: number) => `need ≈ ${need} of ${capacity}`,
    free: 'free',
    retained: 'retained',
    copy: (cidr: string) => `Copy ${cidr}`,
    history: 'History',
    historyColumns: { cidr: 'CIDR', state: 'State', deployment: 'Deployment', reusable: 'Reusable' },
    reusableAfter: (date: string) => `reusable after ${date}`,
    released: 'released',
    noRegions: 'No region blocks yet.',
    expand: (name: string) => `Expand ${name}`,
    collapse: (name: string) => `Collapse ${name}`,
    // The Edit dialog
    editTitle: 'Edit address plan',
    save: 'Save',
    saving: 'Saving…',
    checking: 'Checking…',
    checkFailed: 'The plan couldn’t be checked.',
    changedMeanwhile: (network: string) =>
      `${network} was changed meanwhile; this shows its current address plan with your edits. Check and save again.`,
    saved: 'Address plan saved. Nothing changes in the cloud until the next apply.',
  },
  // 6.15.9 Private DNS
  dns: {
    heading: 'Records are written by the network only.',
    empty: 'No zones yet: the network creates them when an attached deployment needs one.',
    privateLink: 'private link',
    internal: 'internal',
    linked: (n: number) => `linked to ${plural(n, 'spoke')}`,
    route53Zone: 'Route 53 private hosted zone',
    associatedVpcs: (n: number) => `Associated with ${plural(n, 'VPC')}`,
    linkItem: (deployment: string, region: string) => `${deployment} · ${region}`,
    columns: { name: 'Name', type: 'Type', value: 'Value', deployment: 'Deployment', resource: 'Resource' },
    records: (zone: string) => `Records of ${zone}`,
    noRecords: 'No records yet.',
    conflict: (name: string, owner: string, other: string) =>
      `${name} belongs to ${owner}; ${other}’s record isn’t written.`,
  },
  // 6.15.10 Entry points
  entryPoints: {
    title: 'Entry points',
    none: 'No entry points yet.',
    add: 'Add entry point',
    pickerTitle: 'Add entry point',
    search: 'Search edges and add-ons, for example WAF or Shield',
    kinds: 'Entry point kinds',
    available: (network: string, n: number) => `Available in ${network} (${n})`,
    notAvailable: (n: number) => `Not available here (${n})`,
    noneAvailable: 'No entry point kind is available in this installation yet.',
    noMatch: 'No kind matches the search.',
    continueWith: (kind: string) => `Continue with ${kind}`,
    continue: 'Continue',
    chooseKind: 'Choose an available kind.',
    comingLater: 'Coming later',
    disabled: 'Switched off in this installation',
    needsProvider: (provider: string, network: string) => `Needs ${provider} in ${network}`,
    scope: { global: 'Global', regional: 'Regional' } as Record<string, string>,
    tierCost: (tier: string, currency: string, monthly: number) =>
      `${tier} ${currency} ${monthly.toLocaleString('en-US')}/month`,
    phaseThree: 'The entry point form comes with a later version of Nebisys.',
  },
  // 6.15.12 Settings
  settings: {
    nav: 'Settings sections',
    sections: {
      general: 'General',
      providers: 'Providers and regions',
      'address-plan': 'Address plan',
      topology: 'Topology',
      firewall: 'Firewall',
      dns: 'DNS',
      danger: 'Danger zone',
    },
    sectionChanged: (section: string) => `${section} (changed)`,
    changed: 'changed',
    readOnly: 'You need the network admin role to change the network.',
    name: 'Name',
    description: 'Description',
    useProvider: (provider: string) => `Use ${provider}`,
    switchedOff: 'Switched off in this installation.',
    connectivityInUse: (n: number) =>
      `${plural(n, 'deployment is', 'deployments are')} attached through this account.`,
    regionInUse: (n: number, region: string) => `${plural(n, 'attachment')} in ${region}`,
    accountInUse: (n: number) =>
      `${plural(n, 'deployment attaches', 'deployments attach')} with this account.`,
    allowedAccounts: 'Allowed accounts',
    regions: 'Regions',
    removeRegion: (region: string) => `Remove ${region}`,
    addRegion: 'Add region',
    noRegions: 'No regions.',
    notUsed: 'Not used',
    editInTab: 'Edit in the Address plan tab',
    supernets: 'Supernets',
    reserved: 'Reserved ranges',
    lengths: 'Prefix lengths',
    lengthsValue: (region: number, hub: number) => `region /${region} · hub /${hub}`,
    defaultSize: 'Default attachment size',
    topologyLegend: 'Topology',
    internalZone: 'Internal zone',
    internalZoneHelp: 'Optional DNS name for the network’s own records, for example prod.nebisys.internal.',
    privateLinkZones: 'Private link zones',
    auto: 'As attachments need them',
    all: 'All zones',
    destroy: 'Destroy…',
    delete: 'Delete',
    destroyHelp:
      'Destroy deletes what the network created in the cloud; its addresses stay reserved. Delete removes a new or destroyed network.',
    saveBar: 'Unsaved network settings',
    changes: (n: number) => plural(n, 'change'),
    updating: 'Updating…',
    impactUnknown: 'Impact unknown',
    recreated: (names: string) => `re-creates the spokes of ${names}`,
    discard: 'Discard',
    save: 'Save settings',
    saving: 'Saving…',
    saved: (network: string) => `Settings saved. Nothing changes in the cloud until ${network}’s next apply.`,
    errorSummary: (n: number) => `${plural(n, 'problem')} to fix before saving`,
    rebased: 'Someone else changed the network meanwhile; your changes were kept on top of theirs.',
    conflict: (fields: string) =>
      `Someone else changed ${fields} meanwhile; their values are shown. Check and save again.`,
    leaveTitle: 'Leave without saving?',
    leaveMessage: 'The network’s settings have unsaved changes.',
    stay: 'Stay',
    leave: 'Leave',
    none: 'None',
  },
  // Topologies (New network step 4, Settings)
  topology: {
    isolated:
      'Attachments can’t reach each other. Each gets addresses from the plan and the network’s private DNS.',
    mesh: 'Attachments reach each other directly.',
    'hub-spoke': 'Attachments reach each other through a hub in each region.',
    'micro-segmentation': 'Only the traffic network requests allow flows.',
  } as Record<string, string>,
  verification: (state: string) => state,
};
